import { FormEvent, useState } from "react";
import {
  useQuery,
  getBuildingServiceRequests,
  createBuildingServiceRequest,
  updateBuildingServiceRequestStatus,
  getUserMembership,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { ClipboardList, Plus } from "lucide-react";
import { DashboardLayout } from "../../../client/components/Sidebar";
import { useLocationContext } from "../../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../../client/hooks/useRequireSectionVisible";
import { Link } from "react-router-dom";

const STATUSES = ["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"] as const;

export default function WorkOrdersPage() {
  useRequireSectionVisible("showWorkOrder");
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const locationId = selectedLocation?.id || "";

  const { data: membership } = useQuery(
    getUserMembership,
    { locationId },
    { enabled: !!locationId && !!user },
  );
  const mem = membership as any;
  const isManager =
    !!user?.isAdmin ||
    (mem && ["ADMIN", "MANAGER"].includes(mem.role));
  const canSubmit =
    !!user?.isAdmin ||
    !!mem?.canRequestBuildingServices ||
    (mem && ["ADMIN", "MANAGER"].includes(mem.role));

  const { data: rows, isLoading, refetch } = useQuery(
    getBuildingServiceRequests,
    { locationId },
    { enabled: !!locationId },
  );

  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("GENERAL");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!locationId) return;
    setBusy(true);
    setError("");
    try {
      await createBuildingServiceRequest({
        locationId,
        title,
        description,
        category,
      });
      setTitle("");
      setDescription("");
      setShowForm(false);
      await refetch();
    } catch (err: any) {
      setError(err?.message || "Could not submit request");
    } finally {
      setBusy(false);
    }
  }

  const list = (rows as any[]) || [];

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-sky-700">
              Building management
            </p>
            <h1 className="mt-1 text-3xl font-bold">Work orders</h1>
            <p className="mt-2 text-neutral-600">
              Service requests to building management. Only approved tenants can
              submit.
            </p>
          </div>
          <div className="flex gap-2">
            {isManager && (
              <Link
                to="/bm-access"
                className="rounded-xl border border-neutral-300 px-3 py-2 text-sm font-semibold"
              >
                Members
              </Link>
            )}
            {canSubmit && (
              <button
                type="button"
                onClick={() => setShowForm((v) => !v)}
                className="inline-flex items-center gap-2 rounded-xl bg-neutral-900 px-4 py-2 text-sm font-semibold text-white"
              >
                <Plus className="h-4 w-4" />
                New request
              </button>
            )}
          </div>
        </div>

        {!locationId && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Select a location to view requests.
          </p>
        )}

        {locationId && !canSubmit && (
          <p className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            You can view community content, but building management must approve
            you before you can submit service requests.
            {selectedLocation?.locationCode && (
              <>
                {" "}
                <Link
                  to={`/locations/${selectedLocation.locationCode}`}
                  className="font-semibold underline"
                >
                  Check access status
                </Link>
              </>
            )}
          </p>
        )}

        {showForm && (
          <form
            onSubmit={onCreate}
            className="mb-6 space-y-3 rounded-2xl border border-neutral-200 bg-white p-5"
          >
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Title</span>
              <input
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2"
                placeholder="Leaky faucet in unit 1204"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Category</span>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2"
              >
                <option value="GENERAL">General</option>
                <option value="PLUMBING">Plumbing</option>
                <option value="ELECTRICAL">Electrical</option>
                <option value="HVAC">HVAC</option>
                <option value="ACCESS">Access / keys</option>
                <option value="OTHER">Other</option>
              </select>
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Details</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-xl border border-neutral-300 px-3 py-2"
                rows={3}
              />
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="rounded-xl bg-sky-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {busy ? "Submitting…" : "Submit request"}
            </button>
          </form>
        )}

        {isLoading && <p className="text-neutral-500">Loading…</p>}

        <div className="space-y-3">
          {list.map((row) => {
            const status = (row.metadata as any)?.status || "OPEN";
            return (
              <article
                key={row.id}
                className="rounded-2xl border border-neutral-200 bg-white p-4"
              >
                <div className="flex gap-3">
                  <div className="rounded-xl bg-amber-50 p-2 text-amber-700">
                    <ClipboardList className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <h2 className="font-semibold">{row.title}</h2>
                      <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                        {status.replace("_", " ")}
                      </span>
                    </div>
                    <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                      {row.category}
                      {isManager && row.author?.email
                        ? ` · ${row.author.email}`
                        : ""}
                    </p>
                    {row.description && (
                      <p className="mt-1 text-sm text-neutral-600">
                        {row.description}
                      </p>
                    )}
                    {isManager && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {STATUSES.map((s) => (
                          <button
                            key={s}
                            type="button"
                            disabled={status === s}
                            className="rounded-lg border px-2 py-1 text-xs font-medium disabled:opacity-40"
                            onClick={async () => {
                              await updateBuildingServiceRequestStatus({
                                id: row.id,
                                status: s,
                              });
                              await refetch();
                            }}
                          >
                            {s.replace("_", " ")}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
          {locationId && !isLoading && list.length === 0 && (
            <p className="rounded-2xl border border-dashed border-neutral-200 px-4 py-10 text-center text-neutral-500">
              No service requests yet.
            </p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
